import React, { useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, CheckCircle2, Circle, XCircle, Calendar, Package, IndianRupee } from "lucide-react";
import { api, fmtINR } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FLOW = ["booked", "confirmed", "in_progress", "completed"];

export default function TrackPage() {
  const { t } = useLang();
  const [bn, setBn] = useState("");
  const [contact, setContact] = useState("");
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);

  const track = async () => {
    if (!bn || !contact) return toast.error("Enter booking number and contact");
    setLoading(true);
    try {
      const { data } = await api.get("/bookings/track", { params: { booking_number: bn.trim(), contact: contact.trim() } });
      setBooking(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Not found");
      setBooking(null);
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this booking? 75% of paid advance will be refunded as per policy.")) return;
    try {
      const { data } = await api.post(`/bookings/${booking.booking_number}/cancel`, null, { params: { contact: contact.trim() } });
      toast.success(`Cancelled. Refund: ${fmtINR(data.refund_amount)}`);
      track();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    }
  };

  const idx = booking ? FLOW.indexOf(booking.status) : -1;
  const cancelled = booking?.status === "cancelled";

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-8 py-16">
      <div className="text-center mb-10">
        <h1 className="font-display text-4xl sm:text-5xl text-zinc-900">{t("track_title")}</h1>
      </div>

      <div className="bg-card border border-pink-200 rounded-2xl p-6 sm:p-8">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-zinc-700">{t("booking_number")}</Label>
            <Input data-testid="track-bn-input" value={bn} onChange={(e) => setBn(e.target.value)} placeholder="MX..." className="mt-2 bg-white border-pink-200 text-zinc-900" />
          </div>
          <div>
            <Label className="text-zinc-700">{t("contact_verify")}</Label>
            <Input data-testid="track-contact-input" value={contact} onChange={(e) => setContact(e.target.value)} className="mt-2 bg-white border-pink-200 text-zinc-900" />
          </div>
        </div>
        <Button onClick={track} disabled={loading} data-testid="track-btn" className="mt-5 gold-gradient text-white font-semibold rounded-full px-8">
          {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Search size={16} className="mr-2" />} {t("track_btn")}
        </Button>
      </div>

      {booking && (
        <div className="mt-8 bg-card border border-pink-200 rounded-2xl p-6 sm:p-8" data-testid="track-result">
          <div className="flex flex-wrap justify-between gap-4 items-start">
            <div>
              <span className="text-xs text-zinc-500">{t("booking_number")}</span>
              <div className="text-xl font-display gold-text">{booking.booking_number}</div>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${cancelled ? "bg-red-500/10 text-red-400" : booking.payment_status === "paid" ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"}`} data-testid="track-status">
              {t(`status_${booking.status}`)}
            </span>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mt-6 text-sm">
            <div className="flex items-center gap-2 text-zinc-700"><Package size={16} className="text-gold" /> {booking.package_name_en}</div>
            <div className="flex items-center gap-2 text-zinc-700"><Calendar size={16} className="text-gold" /> {booking.event_date}</div>
            <div className="flex items-center gap-2 text-zinc-700"><IndianRupee size={16} className="text-gold" /> Adv {fmtINR(booking.advance_amount)} / {fmtINR(booking.package_price_locked)}</div>
          </div>

          {booking.payment_status === "submitted" && (
            <div className="mt-5 text-sm rounded-lg bg-amber-500/10 text-amber-400 p-3">Payment reference received — awaiting verification by our team.</div>
          )}

          {!cancelled && (
            <div className="mt-8">
              <div className="flex items-center justify-between relative">
                {FLOW.map((s, i) => {
                  const active = idx >= i;
                  return (
                    <div key={s} className="flex flex-col items-center gap-2 flex-1 relative z-10">
                      {active ? <CheckCircle2 className="text-gold" size={22} /> : <Circle className="text-zinc-600" size={22} />}
                      <span className={`text-[11px] text-center ${active ? "text-gold" : "text-zinc-500"}`}>{t(`status_${s}`)}</span>
                    </div>
                  );
                })}
                <div className="absolute top-[11px] left-0 right-0 h-px bg-white/10" />
              </div>
            </div>
          )}

          {cancelled && (
            <div className="mt-6 flex items-center gap-2 text-sm text-red-400"><XCircle size={16} /> Cancelled. Refund: {fmtINR(booking.refund_amount || 0)}</div>
          )}

          {booking.invoice && (
            <div className="mt-6 rounded-xl bg-white border border-pink-200 p-4 text-sm">
              <div className="flex justify-between"><span className="text-zinc-600">Invoice</span><span className="text-gold">{booking.invoice.invoice_number}</span></div>
              <div className="flex justify-between mt-1"><span className="text-zinc-600">Total (incl. GST)</span><span className="text-zinc-900">{fmtINR(booking.invoice.total)}</span></div>
              <div className="flex justify-between mt-1"><span className="text-zinc-600">Balance Due</span><span className="text-zinc-900">{fmtINR(booking.invoice.balance_due)}</span></div>
            </div>
          )}

          <p className="text-[11px] text-zinc-500 mt-6">{t("refund_note")}</p>
          {!cancelled && booking.status !== "completed" && (
            <Button onClick={cancel} data-testid="cancel-booking-btn" variant="outline" className="mt-4 border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-full">{t("cancel_booking")}</Button>
          )}
        </div>
      )}
    </div>
  );
}
